import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { createApi, memorySessionStore, webSessionStore, type SessionStore } from './api';
import { AppRoutes, ROUTER_FUTURE } from './App';
import { Providers } from './Providers';
import { whenQuiet } from './quiet';
import { bootRoute } from './route-prefetch';
import './styles/global.css';
// Shared by the shell and every signed-in screen: in the first bundle, not two tiny chunks
// that would each cost a request while a cold load opens its first screen
import './components/useResource';
import './components/ui/avatar';
import { applyColorMode, readColorMode } from './theme/color-mode';

/** Set once the web fonts have loaded in this browser (they are then in its HTTP cache) */
const FONTS_CACHED_KEY = 'ui.fontsCached';

function readFlag(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === '1';
  } catch (error) {
    console.warn('[web] could not read a local preference', error);
    return false;
  }
}

function writeFlag(key: string): void {
  try {
    window.localStorage.setItem(key, '1');
  } catch (error) {
    console.warn('[web] could not save a local preference', error);
  }
}

/** Proxied to wrangler dev on :8787 by vite.config.ts, so the API is same-origin in dev. */
const DEV_API_URL = '/api/v1';

function resolveApiUrl(): string | null {
  const configured = import.meta.env.VITE_API_URL;
  if (typeof configured === 'string' && configured.trim()) return configured.trim();
  return import.meta.env.DEV ? DEV_API_URL : null;
}

function sessionStore(): SessionStore {
  try {
    return webSessionStore(window.sessionStorage);
  } catch (err) {
    console.warn('[web] sessionStorage unavailable; sessions will not survive a reload', err);
    return memorySessionStore();
  }
}

// Before the first render, so a dark-mode page does not flash light
applyColorMode(readColorMode());

const container = document.getElementById('root');
if (!container) throw new Error('Missing #root element in index.html');
const root = createRoot(container);
const baseUrl = resolveApiUrl();

if (!baseUrl) {
  root.render(
    <main className="page page-narrow px-4 py-10">
      <h1>Configuration needed</h1>
      <p>This build has no API address. Rebuild with VITE_API_URL set (see apps/web/README.md).</p>
    </main>
  );
} else {
  // The refresh token lives in an httpOnly cookie set by the API (never in script-readable storage).
  const api = createApi({ baseUrl, store: sessionStore(), refreshTransport: 'cookie' });
  // A stored session: resume it, and start this screen's chunks and data, before the first render
  bootRoute(api, window.location);
  // Web fonts: on a first visit, once the first screen's own requests are done (text shows in
  // the system font until then, as it did while they downloaded): 56 KB that no longer competes
  // with them. Once they have loaded here, later visits find them in the browser cache and load
  // them at once, so a returning visitor never sees the font change.
  const loadFonts = () =>
    import('./web-fonts').then(
      () => writeFlag(FONTS_CACHED_KEY),
      (error: unknown) => console.warn('[web] the web fonts could not be loaded; the system font stays', error)
    );
  if (readFlag(FONTS_CACHED_KEY)) void loadFonts();
  else whenQuiet(() => void loadFonts());
  root.render(
    <StrictMode>
      <BrowserRouter future={ROUTER_FUTURE}>
        <Providers api={api}>
          <AppRoutes />
        </Providers>
      </BrowserRouter>
    </StrictMode>
  );
}
